import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Crown, AlertCircle, Gem, Copy, TrendingUp, TrendingDown, Dices } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useBattleStore from '../store/useBattleStore'
import useAppStore from '../../../store/useAppStore'
import BattleSlot from '../components/BattleSlot'
import BattleWinner from '../components/BattleWinner'
import { battleApi } from '../services/battleApi'
import AnimatedButton from '../../../components/AnimatedButton'
import audioService from '../../../services/audioService'

const BattlePage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAppStore()
  const { battles, isLoading, error, loadBattles, joinBattle, updateBattle } = useBattleStore()
  const [isJoining, setIsJoining] = useState(false)
  
  // Определяем откуда пришли (из истории или из списка активных баттлов)
  const fromHistory = location.state?.from === 'history'
  
  // Локальное состояние для текущего баттла (обновляется через polling)
  const [currentBattle, setCurrentBattle] = useState(null)
  const [pollingError, setPollingError] = useState(null)
  const [previousStatus, setPreviousStatus] = useState(null)
  
  // Состояния для анимации показа результатов
  const [isShowingResults, setIsShowingResults] = useState(false)
  const [revealedPlayers, setRevealedPlayers] = useState([]) // Массив индексов раскрытых игроков
  const [showWinner, setShowWinner] = useState(false)
  
  // Состояния для управления рулетками
  const [currentRouletteIndex, setCurrentRouletteIndex] = useState(-1) // Индекс текущей крутящейся рулетки (-1 = нет активной)
  const [finishedRoulettes, setFinishedRoulettes] = useState([]) // Массив индексов игроков, завершивших рулетку

  // Флаг: пользователь закрыл победный оверлей (нужен, чтобы после закрытия показать нижние кнопки)
  const [winnerOverlayClosed, setWinnerOverlayClosed] = useState(false)
  
  // Ref для хранения ID интервала polling
  const pollingIntervalRef = useRef(null)
  const revealTimeoutRef = useRef(null)
  const playersBlockRef = useRef(null) // Ref для блока участников
  const playerSlotRefs = useRef([])
  const previousPlayersCountRef = useRef(0) // Ref для отслеживания количества игроков

  const scrollToPlayerSlot = (slotIndex) => {
    const el = playerSlotRefs.current?.[slotIndex]
    if (!el) return

    const rect = el.getBoundingClientRect()
    const top = rect.top + (window.pageYOffset || 0)
    const offset = 88

    window.scrollTo({
      top: Math.max(0, top - offset),
      behavior: 'smooth'
    })
  }

  // Функция для загрузки данных баттла через API
  const fetchBattleData = async () => {
    try {
      const battleData = await battleApi.getBattleHistory(id)
      
      // Детальное логирование ПОЛНОГО ответа API
      console.log('📦 FULL battle data from API:', battleData)
      console.log('📦 Battle status:', battleData.status)
      console.log('📦 Players:', battleData.players)
      console.log('📦 result_casebattle:', battleData.result_casebattle)
      
      // Проверяем присоединение новых игроков
      const currentPlayersCount = battleData.players?.length || 0
      const previousPlayersCount = previousPlayersCountRef.current
      
      // Воспроизводим звук если количество игроков увеличилось
      // И это не первая загрузка (previousPlayersCount > 0)
      if (previousPlayersCount > 0 && currentPlayersCount > previousPlayersCount) {
        console.log(`🔊 New player joined! ${previousPlayersCount} -> ${currentPlayersCount}`)
        audioService.play('userJoinBattle')
      }
      
      // Обновляем счетчик игроков
      previousPlayersCountRef.current = currentPlayersCount
      
      setCurrentBattle(battleData)
      setPollingError(null)
      
      // Обновляем баттл в store для синхронизации
      updateBattle(battleData)
      
      return battleData
    } catch (error) {
      console.error('❌ Polling error:', error)
      setPollingError(error.message || 'Ошибка загрузки данных баттла')
      
      // Останавливаем polling при ошибке
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      
      throw error
    }
  }

  // Начальная загрузка данных баттла при монтировании
  useEffect(() => {
    fetchBattleData()
  }, [id])

  // Polling: обновляем данные баттла каждую секунду
  useEffect(() => {
    // Запускаем polling только если баттл в статусе waiting
    // При статусах raffle и finished polling не нужен
    if (currentBattle?.status === 'waiting') {
      pollingIntervalRef.current = setInterval(() => {
        fetchBattleData()
      }, 1000) // 1 секунда
      
      console.log('🔄 Polling started for battle:', id)
    } else {
      // Останавливаем polling если статус изменился на raffle или finished
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        console.log('⏹️ Polling stopped - status:', currentBattle?.status)
      }
    }
    
    // Cleanup: останавливаем polling при размонтировании или смене статуса
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        console.log('⏹️ Polling stopped')
      }
    }
  }, [currentBattle?.status, id])

  // Коллбэк для завершения рулетки одного игрока
  const handleRouletteComplete = (playerIndex) => {
    console.log(`✅ Roulette completed for player ${playerIndex + 1}`)
    // Рулетка завершена, можно переходить к следующему игроку
  }

  // Функция для последовательного показа результатов игроков с рулетками
  const startResultsReveal = async (playersCount) => {
    console.log('🎬 Starting results reveal animation...')
    setIsShowingResults(true)
    setRevealedPlayers([])
    setShowWinner(false)
    setWinnerOverlayClosed(false)
    setCurrentRouletteIndex(-1)
    setFinishedRoulettes([]) // Сбрасываем список завершенных рулеток
    
    // Прокручиваем к блоку участников
    // if (playersBlockRef.current) {
    //   playersBlockRef.current.scrollIntoView({ 
    //     behavior: 'smooth', 
    //     block: 'start' 
    //   })
    // }
    
    // Пауза 1 секунду перед показом первого игрока
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Последовательно показываем результаты каждого игрока с рулетками
    for (let i = 0; i < playersCount; i++) {
      console.log(`🎯 Revealing player ${i + 1}/${playersCount}`)
      
      // Раскрываем игрока (показываем его слот с результатом)
      setRevealedPlayers(prev => [...prev, i])
      
      // Небольшая пауза чтобы DOM успел обновиться
      await new Promise(resolve => setTimeout(resolve, 100))

      scrollToPlayerSlot(i)

      // Пауза, чтобы автоскролл успел начаться до старта рулетки
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Запускаем рулетку для этого игрока
      setCurrentRouletteIndex(i)
      
      // Ждем завершения рулетки (4 секунды анимации)
      await new Promise(resolve => setTimeout(resolve, 4000))
      
      // Добавляем индекс в список завершенных рулеток
      setFinishedRoulettes(prev => [...prev, i])
      
      // Сбрасываем индекс текущей рулетки
      setCurrentRouletteIndex(-1)
      
      // Пауза перед следующим игроком (кроме последнего)
      if (i < playersCount - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
    }
    
    // Пауза 1 секунда перед показом победителя
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Показываем победителя с конфетти
    console.log('🏆 Showing winner!')
    setShowWinner(true)
    setIsShowingResults(false)
  }

  // Отслеживаем смену статуса с waiting на raffle/finished или cancelled
  useEffect(() => {
    if (!currentBattle) return
    
    const currentStatus = currentBattle.status
    
    // Если статус изменился на cancelled (таймаут)
    if (currentStatus === 'cancelled' && previousStatus !== 'cancelled') {
      console.log('❌ Battle cancelled due to timeout')
      
      // Останавливаем polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      
      // Показываем toast и редиректим на /battles
      toast.error(t('battle.cancelled'))
      setTimeout(() => navigate('/battles'), 2000)
      
      // Обновляем предыдущий статус и выходим
      setPreviousStatus(currentStatus)
      return
    }
    
    // Если статус изменился с waiting на raffle - запускаем анимацию розыгрыша
    if (previousStatus === 'waiting' && currentStatus === 'raffle') {
      console.log('� Battle raffle started! Showing animations...')
      
      // Останавливаем polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      
      // Запускаем анимацию показа результатов
      const playersCount = currentBattle.players?.length || 0
      if (playersCount > 0) {
        startResultsReveal(playersCount)
      }
    }
    
    // Если попали на страницу с уже статусом raffle - запускаем анимацию
    if (previousStatus === null && currentStatus === 'raffle' && !isShowingResults && !showWinner) {
      console.log('� Landed on raffle battle! Showing animations...')
      const playersCount = currentBattle.players?.length || 0
      if (playersCount > 0) {
        startResultsReveal(playersCount)
      }
    }
    
    // Если попали на страницу с уже завершенным баттлом (finished) - показываем статичный результат БЕЗ анимации
    if (previousStatus === null && currentStatus === 'finished' && !isShowingResults && !showWinner) {
      console.log('🏁 Landed on finished battle! Showing static results (no animations)...')
      // Просто показываем победителя без анимации
      setShowWinner(true)
      setWinnerOverlayClosed(true)
    }
    
    // Обновляем предыдущий статус
    setPreviousStatus(currentStatus)
  }, [currentBattle?.status, previousStatus, isShowingResults, showWinner])
  
  // Cleanup для таймеров анимации
  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current)
      }
    }
  }, [])

  // Обработчик присоединения к баттлу
  const handleJoin = async () => {
    if (!currentBattle) return

    // Проверяем баланс на фронтенде перед запросом
    if (user.balance < currentBattle.need_balance) {
      toast.error(t('battle.joinNotEnough'))
      setTimeout(() => navigate('/payment'), 1000)
      return
    }

    // Отправляем цель в Яндекс.Метрику
    if (window.ym) {
      window.ym(104258304, 'reachGoal', 'join-case-battle')
    }

    try {
      setIsJoining(true)
      
      // Присоединяемся к баттлу
      const result = await joinBattle(id)
      
      // Сразу обновляем данные баттла после присоединения
      await fetchBattleData()
      
      // Показываем уведомление о присоединении
      toast.success(t('battle.joinSuccess'))
      
    } catch (error) {
      // Обработка специфичных ошибок
      if (error.message === 'not_enough_balance') {
        toast.error(t('battle.joinNotEnough'))
        setTimeout(() => navigate('/payment'), 1000)
      } else if (error.message === 'error backend') {
        toast.error(t('battle.joinServerError'))
      } else {
        toast.error(error.message || t('battle.joinError'))
      }
    } finally {
      setIsJoining(false)
    }
  }


  const handleNewBattle = () => {
    navigate('/battles/create')
  }

  const handleGoToBattles = () => {
    navigate('/battles')
  }

  // Показываем loader только при первой загрузке
  if (!currentBattle && !pollingError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    )
  }

  // Показываем ошибку если не удалось загрузить данные баттла
  if (pollingError && !currentBattle) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-medium">{t('battle.error')}</p>
            <p className="text-red-300 text-sm">{pollingError}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/battles')}
          className="mt-4 px-6 py-3 bg-surface text-white rounded-lg hover:bg-surface/80 transition-colors"
        >
          {t('battle.backToList')}
        </button>
      </div>
    )
  }

  if (!currentBattle) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-surface rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <div>
            <p className="text-white font-medium">{t('battle.error')}</p>
            <p className="text-text-secondary text-sm">{t('battle.backToList')}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/battles')}
          className="mt-4 px-6 py-3 bg-surface text-white rounded-lg hover:bg-surface/80 transition-colors"
        >
          {t('battle.backToList')}
        </button>
      </div>
    )
  }

  const { status, case: caseData, players = [], count_players, count_case, battle_close_in_seconds, you, you_win, result_casebattle, type } = currentBattle
  const countCaseValue = Number(count_case) || 1
  
  // Обрабатываем игроков - результаты уже есть в массиве players
  const playersWithResults = players.map(player => {
    // Проверяем, есть ли у игрока выигранные скины
    const hasSkins = player.skins && player.skins.length > 0
    
    console.log(`🔍 Player slot ${player.slot}:`, {
      playerSlot: player.slot,
      hasSkins: hasSkins,
      skinsCount: player.skins?.length || 0,
      sum: player.sum,
      win: player.win
    })
    
    // Если у игрока есть скины, добавляем первый скин как result для отображения в BattleSlot
    if (hasSkins) {
      return {
        ...player,
        result: player.skins[0], // Берем первый скин как результат для отображения
        sum: player.sum,
        win: parseInt(player.win), // Преобразуем "1"/"0" в число
        allSkins: player.skins, // Сохраняем все скины
        items: player.skins // Для BattleWinner компонента
      }
    }
    
    return player
  })
  
  // Логирование для отладки
  console.log('🎮 Battle status:', status)
  console.log('👥 Players with results:', playersWithResults.filter(p => p.result).length, '/', playersWithResults.length)
  
  // Определяем победителя для raffle и finished баттлов (win === 1)
  // Winner определяется для обоих статусов, так как данные API одинаковые
  let winner = (status === 'raffle' || status === 'finished') 
    ? playersWithResults.find(p => p.win === 1) 
    : null
  
  // Если есть победитель, собираем все скины от всех игроков
  if (winner) {
    const allSkins = []
    let totalSum = 0
    
    playersWithResults.forEach(player => {
      if (player.skins && player.skins.length > 0) {
        allSkins.push(...player.skins)
        totalSum += parseInt(player.sum) || 0
      }
    })
    
    // Обновляем победителя с полным списком скинов и общей суммой
    winner = {
      ...winner,
      items: allSkins,
      totalPrize: totalSum
    }
    
    console.log('🏆 Winner:', `${winner.user_name} (slot ${winner.slot})`)
    console.log('💎 Total prize:', totalSum, 'crystals')
    console.log('🎁 Total items:', allSkins.length)
  }

  
  // Проверяем, является ли текущий пользователь создателем
  const isCreator = you === 1 && players[0]?.you === 1
  
  const battleTypeLabel = type === 'less'
    ? t('battle.mode.less')
    : type === 'random'
    ? t('battle.mode.random')
    : t('battle.mode.more')
  const BattleTypeIcon = type === 'less' ? TrendingDown : type === 'random' ? Dices : TrendingUp

  // Можно присоединиться если статус waiting и есть свободные слоты
  // НЕ показываем кнопку если пользователь уже участвует в баттле
  const currentPlayers = players.length
  const totalSlots = parseInt(count_players)
  const availableSlots = totalSlots - currentPlayers
  const canJoin = status === 'waiting' && availableSlots > 0 && !you

  return (
    <div className="pb-12 lg:container lg:mx-auto lg:px-4 lg:py-6">
      <div className="hidden lg:flex lg:items-center lg:gap-4 lg:mb-6 lg:w-[420px]">
        <button
          onClick={() => navigate(fromHistory ? '/battles/history' : '/battles')}
          className="p-2 hover:bg-surface/50 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <span>{t('battle.modePrefix')} {battleTypeLabel}</span>
            <BattleTypeIcon className="w-5 h-5 text-accent-primary" />
          </h1>
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(id)
            toast.success(t('battle.battleIdCopied'))
          }}
          className="p-2 hover:bg-surface/50 rounded-lg transition-colors"
          title={t('battle.copyBattleId')}
        >
          <Copy className="w-5 h-5 text-white" />
        </button>
      </div>
      <div className="lg:flex lg:items-start lg:gap-8">
      {/* Hero Section with Case Image */}
      {caseData && (
        <div className="relative w-full overflow-hidden -mt-6 lg:mt-0 lg:w-[420px] lg:flex-shrink-0 lg:rounded-3xl lg:border lg:border-white/10">
          {/* Background Case Image */}
          <div className="relative w-full">
            <img
              src={caseData.img}
              alt={caseData.name}
              className="w-full h-auto object-contain max-h-[550px]"
            />
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-dark-primary/100 via-dark-primary/0 to-dark-primary/100 pointer-events-none" />
          
          {/* Content Over Image */}
          <div className="absolute inset-0 flex flex-col px-4 min-h-[360px]">
            {/* Header */}
            <div className="flex items-center gap-4 pt-10 pb-3 lg:hidden">
              <button
                onClick={() => navigate(fromHistory ? '/battles/history' : '/battles')}
                className="p-2 hover:bg-surface/50 rounded-lg transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="w-6 h-6 text-white" />
              </button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-white drop-shadow-lg flex items-center gap-2">
                  <span>{t('battle.modePrefix')} {battleTypeLabel}</span>
                  <BattleTypeIcon className="w-5 h-5 text-accent-primary" />
                </h1>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(id)
                  toast.success(t('battle.battleIdCopied'))
                }}
                className="p-2 hover:bg-surface/50 rounded-lg transition-colors backdrop-blur-sm"
                title={t('battle.copyBattleId')}
              >
                <Copy className="w-5 h-5 text-white" />
              </button>
            </div>
            
            {/* Case Info at Bottom */}
            <div className="mt-auto pb-4 text-center">
              <h2 className="text-2xl font-bold text-accent-primary mb-2 drop-shadow-lg">
                {caseData.name}
                {countCaseValue > 1 && (
                  <span className="text-text-secondary ml-2">x{countCaseValue}</span>
                )}
              </h2>
              {/* <div className="inline-flex items-center gap-2 bg-dark-primary/80 backdrop-blur-sm px-5 py-2 rounded-full border border-accent-primary/30">
                <Gem className="w-5 h-5 text-accent-primary" />
                <span className="text-xl font-bold text-accent-primary">{caseData.price?.toLocaleString()}</span>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 lg:flex-1 lg:min-w-0 lg:px-0 lg:py-0">

      {/* Players Slots */}
      <h2 ref={playersBlockRef} className="text-xl font-bold text-white mb-4">{t('battle.participants')}</h2>
      <div className={`grid gap-4 mb-6 ${totalSlots === 2 ? 'grid-cols-2 lg:grid-cols-2' : totalSlots === 3 ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-2 lg:grid-cols-2'}`}>
        {Array.from({ length: parseInt(count_players) }).map((_, index) => {
          const player = playersWithResults[index] || null
          
          // Логика отображения зависит от статуса баттла:
          // 1. finished - показываем результаты сразу БЕЗ анимации рулеток
          // 2. raffle - показываем анимацию рулеток поэтапно
          
          let shouldShowResult = false
          let shouldStartRoulette = false
          let displayPlayer = player
          
          if (status === 'finished') {
            // При статусе finished показываем все результаты сразу (статично)
            shouldShowResult = true
            shouldStartRoulette = false // НЕ запускаем рулетку
            displayPlayer = player // Показываем игрока с результатами
          } else if (status === 'raffle') {
            // При статусе raffle показываем анимацию рулеток поэтапно
            // Показываем слот только если игрок раскрыт И рулетка запущена (или уже завершена)
            const isRevealed = revealedPlayers.includes(index)
            const isCurrentRoulette = currentRouletteIndex === index
            const hasFinishedRoulette = finishedRoulettes.includes(index)
            
            shouldStartRoulette = isCurrentRoulette
            
            // Передаем result только когда рулетка запущена или завершена
            if (isCurrentRoulette || hasFinishedRoulette || showWinner) {
              displayPlayer = player
            } else if (isRevealed) {
              // Игрок раскрыт, но рулетка еще не запущена - показываем без результата
              displayPlayer = player ? { ...player, result: null, skins: null } : null
            } else {
              // Игрок еще не раскрыт
              displayPlayer = player ? { ...player, result: null, skins: null } : null
            }
          } else {
            // Для других статусов (waiting, cancelled) - не показываем результаты
            displayPlayer = player ? { ...player, result: null, skins: null } : null
          }
          
          return (
            <div
              key={index}
              ref={(el) => {
                playerSlotRefs.current[index] = el
              }}
            >
              <BattleSlot
                player={displayPlayer}
                index={index}
                isWinner={showWinner && player && winner?.user_id === player?.user_id}
                caseSkins={caseData?.skins || []}
                shouldStartRoulette={shouldStartRoulette}
                onRouletteComplete={() => handleRouletteComplete(index)}
              />
            </div>
          )
        })}
      </div>

      {/* Winner - отображаем при raffle после анимации или при finished сразу */}
      {showWinner && winner && (
        <BattleWinner 
          winner={winner} 
          totalPrize={winner.totalPrize || 0}
          isCurrentUser={you === 1 && you_win === 1}
          isParticipant={you === 1}
          skipEffects={status === 'finished'} // При finished не воспроизводим звуки и конфетти
          primaryActionLabel={t('battle.newBattle')}
          secondaryActionLabel={t('battle.toBattles')}
          onPrimaryAction={() => {
            setWinnerOverlayClosed(true)
            handleNewBattle()
          }}
          onSecondaryAction={() => {
            setWinnerOverlayClosed(true)
            handleGoToBattles()
          }}
          onClose={() => setWinnerOverlayClosed(true)}
        />
      )}

      {/* Action Buttons */}
      <div className="fixed bottom-16 left-0 right-0 p-4 pb-6 pt-4 flex justify-center">
        <div className="w-full max-w-[600px]">
        {/* Кнопка Войти - если статус waiting и есть свободные слоты */}
        {status === 'waiting' && canJoin && (
          user.balance >= currentBattle.need_balance ? (
            <AnimatedButton
              onClick={handleJoin}
              disabled={isJoining}
              className="py-3 flex items-center justify-center gap-2"
            >
              {isJoining ? (
                t('battle.joining')
              ) : (
                <>
                  {t('battle.join')}
                  <Gem className="w-4 h-4" />
                  {currentBattle.need_balance?.toLocaleString()}
                </>
              )}
            </AnimatedButton>
          ) : (
            <button 
              onClick={() => navigate('/payment')}
              className="w-full py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transform hover:scale-[1.02] active:scale-[0.98] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-blue-500/30"
            >
              {t('cases.topUpOn', { amount: Number(currentBattle.need_balance - user.balance).toLocaleString() })}
              <Gem size={20} />
            </button>
          )
        )}

        {/* Кнопки для завершенного баттла:
            - При raffle: показываем после завершения анимации и показа победителя
            - При finished: показываем сразу (showWinner устанавливается сразу при загрузке) */}
        {((status === 'raffle' && showWinner) || status === 'finished') && winnerOverlayClosed && (
          <div className="flex gap-3">
            <button
              onClick={handleNewBattle}
              className="flex-1 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              {t('battle.newBattle')}
            </button>
            <button
              onClick={handleGoToBattles}
              className="flex-1 py-3 bg-surface text-white rounded-lg font-semibold hover:bg-surface/80 transition-colors border border-white/20"
            >
              {t('battle.toBattles')}
            </button>
          </div>
        )}
        </div>
      </div>
      </div>
      </div>
    </div>
  )
}

export default BattlePage
